import classNames from "classnames";
import { Form, Formik } from "formik";
import { ConfirmOptions, useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import Papa from "papaparse";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { StringSchema } from "yup";

import {
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  makeStyles,
  MenuItem,
  Paper,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip
} from "@material-ui/core";
import Add from "@material-ui/icons/Add";
import CloudDownload from "@material-ui/icons/CloudDownload";
import SearchIcon from "@material-ui/icons/Search";

import palette from "src/styles/palette";
import downloadContent from "src/util/downloadContent";
import yup from "src/util/yup";

import DisabledButtonWithTooltip from "./DisabledButtonWithTooltip";
import FormikTextField from "./form/FormikTextField";
import Select, { MultiSelect } from "./form/Select";

const useStyles = makeStyles(theme => ({
  table: {
    marginBottom: theme.spacing(3)
  }
}));

type RowActionButton<R> = {
  icon: React.ReactNode;
  tooltipLabel?: string;
  onClick: (row: R) => void | Promise<void>;
  confirm?: ConfirmOptions | ((row: R) => ConfirmOptions);
  type?: "button";
};

type RowActionLink<R> = {
  icon: React.ReactNode;
  tooltipLabel?: string;
  href: string | ((row: R) => string);
  type: "link";
};

type RowActionPlaceholder = {
  label: string | JSX.Element;
  type: "placeholder";
};

export type RowAction<R> =
  | RowActionButton<R>
  | RowActionLink<R>
  | RowActionPlaceholder;
export type RowActions<R> = RowAction<R>[];

export type TableColumns<R extends { id: string }> = {
  key: string;
  title?: React.ReactNode;
  renderValue: (row: R) => React.ReactNode;
}[];

export type TableBaseProps<R extends { id: string }> = {
  columns: TableColumns<R>;
  rows: R[];
  rowActions?: RowActions<R> | ((row: R) => RowActions<R>);
  emptyState?: React.ReactNode;
};

export type TablePropsWithFooter<R extends { id: string }> =
  TableBaseProps<R> & {
    noFooter?: false;
    totalCount: number;
    rowsPerPage: number;
    onChangeRowsPerPage: (rowsPerPage: number) => void;
    page: number;
    onChangePage: (page: number) => void;
  };

export type TablePropsWithoutFooter<R extends { id: string }> =
  TableBaseProps<R> & {
    noFooter: true;
  };

export type TableProps<R extends { id: string }> =
  | TablePropsWithFooter<R>
  | TablePropsWithoutFooter<R>;

export default function Table<R extends { id: string }>(
  props: TableProps<R>
): JSX.Element {
  const { columns, rows, rowActions, emptyState, noFooter } = props;

  const classes = useStyles();

  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  function renderRowAction(rowAction: RowAction<R>, row: R): JSX.Element {
    if (rowAction.type === "placeholder") {
      return <Chip label={rowAction.label} variant="outlined" />;
    }

    if (rowAction.type === "link") {
      return <ActionLink row={row} rowAction={rowAction} />;
    }

    return (
      <ActionButton
        row={row}
        rowAction={rowAction}
        loading={loadingIds.has(row.id)}
        setLoading={loading => {
          const newSet = new Set(loadingIds);
          if (loading) {
            newSet.add(row.id);
          } else {
            newSet.delete(row.id);
          }
          setLoadingIds(newSet);
        }}
      />
    );
  }

  return (
    <TableContainer className={classes.table}>
      <MuiTable>
        <TableHead>
          <TableRow>
            {columns.map(({ key, title }) => (
              <TableCell key={key}>{title}</TableCell>
            ))}
            {rowActions ? <TableCell key="actions">Actions</TableCell> : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && emptyState ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                {emptyState}
              </TableCell>
            </TableRow>
          ) : (
            rows.map(row => (
              <TableRow key={row.id}>
                {columns.map(({ key, renderValue }) => (
                  <TableCell key={key}>{renderValue(row)}</TableCell>
                ))}
                {rowActions ? (
                  <TableCell key="actions">
                    {(typeof rowActions === "function"
                      ? rowActions(row)
                      : rowActions
                    ).map(rowAction => renderRowAction(rowAction, row))}
                  </TableCell>
                ) : null}
              </TableRow>
            ))
          )}
        </TableBody>
        {!noFooter && (
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                colSpan={columns.length + (rowActions ? 1 : 0)}
                count={props.totalCount}
                rowsPerPage={props.rowsPerPage}
                page={props.page}
                onPageChange={(_evt, newPage) => props.onChangePage(newPage)}
                onRowsPerPageChange={evt =>
                  props.onChangeRowsPerPage(parseInt(evt.target.value, 10))
                }
              />
            </TableRow>
          </TableFooter>
        )}
      </MuiTable>
    </TableContainer>
  );
}

function ActionButton<R>({
  row,
  rowAction,
  loading,
  setLoading
}: {
  row: R;
  rowAction: RowActionButton<R>;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}) {
  const confirm = useConfirm();

  const handleClick = async () => {
    if (rowAction.confirm) {
      const confirmOptions =
        typeof rowAction.confirm === "function"
          ? rowAction.confirm(row)
          : rowAction.confirm;
      try {
        await confirm(confirmOptions);
      } catch {
        // user canceled
        return;
      }
    }

    setLoading(true);
    await rowAction.onClick(row);
    setLoading(false);
  };

  if (loading) {
    return <CircularProgress size={24} />;
  }

  const button = (
    <IconButton onClick={handleClick}>{rowAction.icon}</IconButton>
  );

  if (rowAction.tooltipLabel) {
    return <Tooltip title={rowAction.tooltipLabel}>{button}</Tooltip>;
  }

  return button;
}

function ActionLink<R>({
  row,
  rowAction
}: {
  row: R;
  rowAction: RowActionLink<R>;
}) {
  const to =
    typeof rowAction.href === "function" ? rowAction.href(row) : rowAction.href;

  const link = (
    <IconButton component={Link} to={to}>
      {rowAction.icon}
    </IconButton>
  );

  if (rowAction.tooltipLabel) {
    return <Tooltip title={rowAction.tooltipLabel}>{link}</Tooltip>;
  }

  return link;
}

const useTableControlsStyles = makeStyles(theme => ({
  controlsPaper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    paddingBottom: 0,
    backgroundColor: "#F0EEF5"
  },
  controlsRow: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row"
  },
  controlsFirstRow: {
    paddingRight: 35,
    "@media(max-width: 1000px)": {
      flexDirection: "column"
    }
  },
  buttonWrapper: {
    display: "flex",
    marginBottom: theme.spacing(2),
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  button: {
    maxWidth: "250px"
  },
  dropdown: {
    marginRight: theme.spacing(3),
    marginBottom: theme.spacing(2),
    minWidth: 160
  },
  searchForm: {
    flex: 1
  },
  helpText: {
    marginBottom: theme.spacing(2),
    color: palette.darkGray
  }
}));

type Filter = {
  label: React.ReactNode;
  options: { value: string; label: React.ReactNode }[];
} & (
  | {
      value: string;
      onChange: (value: string) => void;
      multi?: false;
    }
  | {
      value: string[];
      onChange: (value: string[]) => void;
      multi: true;
    }
);

type ButtonProps = {
  label: React.ReactNode;
  onClick: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  tooltipTitle?: string;
};

type AddButtonProps = ButtonProps;
type ExportButtonProps = Omit<ButtonProps, "onClick"> & {
  getData: () => Promise<{
    data: Record<string, any>[];
    fileName: string;
    options: Papa.UnparseConfig;
  }>;
};

export function TableControls({
  addButtons,
  exportButtons,
  filters,
  sort,
  search,
  helpText
}: {
  addButtons?: AddButtonProps[];
  exportButtons?: ExportButtonProps[];
  filters?: Filter[];
  sort?: {
    value: string;
    onChange: (value: string) => void;
    options: { value: string; label: React.ReactNode }[];
  };
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    schema?: StringSchema;
  };
  helpText?: string;
}): JSX.Element {
  const classes = useTableControlsStyles();

  return (
    <Paper className={classes.controlsPaper} elevation={0}>
      <div
        className={classNames(classes.controlsRow, classes.controlsFirstRow)}
      >
        <div className={classes.buttonWrapper}>
          {addButtons?.map(addButtonProps => <AddButton {...addButtonProps} />)}
          {exportButtons?.map(exportButtonProps => (
            <ExportButton {...exportButtonProps} />
          ))}
        </div>
        <div>
          {filters?.map((filter, fIndex) =>
            filter.multi ? (
              <MultiSelect
                key={`multiSelect_filter_${fIndex}`}
                variant="underlined"
                className={classes.dropdown}
                label={filter.label}
                value={filter.value}
                onChange={event => filter.onChange(event.target.value)}
                renderValue={selectedUnknown => {
                  const selected = selectedUnknown as string[];

                  if (
                    selected.length === 0 ||
                    selected.length === filter.options.length
                  ) {
                    return "All";
                  }

                  if (selected.length === filter.options.length - 1) {
                    // figure out which one isn't selected
                    const notSelected = filter.options.filter(
                      option => !selected.includes(option.value)
                    )[0];

                    return `Not ${notSelected.label}`;
                  }

                  const selectedLabels = selected.map(
                    value => filter.options.find(s => s.value === value)!.label
                  );

                  if (selectedLabels.length === 1) {
                    return selectedLabels[0];
                  }

                  return `${selectedLabels[0]} and ${
                    selectedLabels.length - 1
                  } more`;
                }}
              >
                {filter.options.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </MultiSelect>
            ) : (
              <Select
                key={`multiSelect_filter_${fIndex}`}
                variant="underlined"
                className={classes.dropdown}
                label={filter.label}
                value={filter.value}
                onChange={event => filter.onChange(event.target.value)}
              >
                {filter.options.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            )
          )}
          {sort ? (
            <Select
              variant="underlined"
              className={classes.dropdown}
              label="Sort By"
              value={sort.value}
              onChange={event => sort.onChange(event.target.value)}
            >
              {sort.options.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          ) : null}
        </div>
      </div>
      {search ? (
        <div className={classes.controlsRow}>
          <Formik
            initialValues={{ search: search.value }}
            validationSchema={yup
              .object()
              .shape({ search: search.schema ?? yup.string() })}
            onSubmit={values => search.onChange(values.search)}
          >
            {({ submitForm, errors }) => (
              <Form className={classes.searchForm}>
                <FormikTextField
                  fullWidth
                  name="search"
                  onBlur={submitForm}
                  placeholder={search.placeholder}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={submitForm}>
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  error={!!errors.search}
                  helperText={errors?.search}
                />
              </Form>
            )}
          </Formik>
        </div>
      ) : null}
      {helpText ? (
        <div className={classes.controlsRow}>
          <div className={classes.helpText}>{helpText}</div>
        </div>
      ) : null}
    </Paper>
  );
}

function TableButton({
  label,
  onClick,
  icon,
  disabled,
  tooltipTitle
}: ButtonProps) {
  const classes = useTableControlsStyles();

  if (tooltipTitle && disabled) {
    return (
      <DisabledButtonWithTooltip
        variant="contained"
        color="primary"
        className={classes.button}
        startIcon={icon}
        disabled
        tooltipTitle={tooltipTitle}
      >
        {label}
      </DisabledButtonWithTooltip>
    );
  }

  const button = (
    <Button
      onClick={onClick}
      variant="contained"
      color="primary"
      className={classes.button}
      startIcon={icon}
      disabled={disabled}
    >
      {label}
    </Button>
  );

  if (tooltipTitle) {
    return <Tooltip title={tooltipTitle}>{button}</Tooltip>;
  }

  return button;
}

function AddButton(props: AddButtonProps) {
  return <TableButton icon={<Add />} {...props} />;
}

function ExportButton({
  label,
  getData,
  icon,
  disabled,
  tooltipTitle
}: ExportButtonProps) {
  const [loading, setLoading] = useState(false);
  const snackbar = useSnackbar();

  async function onClick() {
    setLoading(true);

    try {
      const { data, fileName, options } = await getData();
      const csvContent = Papa.unparse(data, options);

      downloadContent(csvContent, { fileName, contentType: "text/csv" });
    } catch (e: any) {
      console.error(e);
      snackbar.enqueueSnackbar(e.message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <TableButton
      icon={icon || <CloudDownload />}
      onClick={onClick}
      disabled={loading || disabled}
      label={label}
      tooltipTitle={tooltipTitle}
    />
  );
}
